import React, { SyntheticEvent, useState } from "react";
import { Box, Typography, Paper, ClickAwayListener, Popper, Fade } from "@mui/material";
import { Check as CheckIcon, KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import useTranslation, { SupportedLanguage } from "../hooks/useTranslation";
import colors from "../themes/colors";
import { dt } from "../themes/redesignTokens";
import customersApi from "../../customer/shared/api/customersApi";
import { LANGUAGE_NATIVE_NAMES } from "../i18n/languages";

interface LanguageSwitcherProps {
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className }) => {
  const { changeLanguage, getCurrentLanguage, getSupportedLanguages } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: SyntheticEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleLanguageSelect = (lng: SupportedLanguage) => {
    changeLanguage(lng);
    customersApi.updateLanguage(lng).catch(() => {
      // silent -- localStorage (i18nextLng) is the fallback
    });
    handleClose();
  };

  const getLanguageDisplayName = (lng: SupportedLanguage): string => LANGUAGE_NATIVE_NAMES[lng];

  const currentLanguage = getCurrentLanguage();

  return (
    <Box className={className}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "fit-content",
          gap: 1,
          padding: "8px 12px",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: colors.primary.white1,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: colors.secondary.gray96
          }
        }}
      >
        <Box
          sx={{
            fontFamily: "Work Sans",
            fontSize: "16px",
            color: colors.secondary.gray20,
            fontWeight: 400
          }}
        >
          {getLanguageDisplayName(currentLanguage)}
        </Box>
        <ArrowDownIcon
          sx={{
            color: dt.blue,
            fontSize: "20px",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease"
          }}
        />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={8}
              sx={{
                mt: 1,
                minWidth: "140px",
                borderRadius: "8px",
                overflow: "hidden",
                border: `1px solid ${colors.secondary.gray90}`
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {getSupportedLanguages().map((lng) => (
                    <Box
                      key={lng}
                      onClick={() => handleLanguageSelect(lng)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        cursor: "pointer",
                        backgroundColor: lng === currentLanguage ? colors.secondary.gray96 : colors.primary.white1,
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: colors.primary.blue3,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: lng === currentLanguage ? dt.blue : colors.secondary.gray20,
                          fontWeight: lng === currentLanguage ? 600 : 400
                        }}
                      >
                        {getLanguageDisplayName(lng)}
                      </Typography>
                      {lng === currentLanguage && (
                        <CheckIcon
                          sx={{
                            color: dt.blue,
                            fontSize: "18px"
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box >
  );
};

export default LanguageSwitcher; 